import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { captureSpPasswordResetCompleted } from '@/lib/analytics'
import { useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import OutlinedInput from '@mui/material/OutlinedInput'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { AuthFormCardSkeleton } from '@/shared/components/skeletons'
import { PasswordVisibilityIcon } from '@/shared/lib/password-visibility'
import brushfireLogo from '@/features/auth/assets/login/brushfire-logo.png'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { runDetached } from '@/shared/lib/runDetachedPromise'
import { useToast } from '@/shared/toast/ToastProvider'
import { ApiError } from '@/api/http/types'
import { useSessionStore } from '@/stores/useSessionStore'
import { queryKeys } from '@/api/query-keys'
import {
  fetchAndApplySession,
  useResetPassword,
  useValidateResetToken,
} from '../api'
import {
  resetPasswordSchema,
  type ResetPasswordFormData,
  PASSWORD_RESET_TOKEN_STATE_KEY,
  readPasswordResetTokenFromState,
} from '../lib'
import { SP_FORGOT_PASSWORD_LOGIN_SUCCESS_MESSAGE } from '../lib/password-messages'
import { PasswordChecklist } from '../components'
import {
  RESET_PASSWORD_MISMATCH_MSG,
  resetPasswordCardSx,
  resetPasswordConfirmFieldBlockSx,
  resetPasswordConfirmWithErrorBlockSx,
  resetPasswordErrorInputSx,
  resetPasswordFailedCardSx,
  resetPasswordFailedMiddleContentSx,
  resetPasswordFailedHeadingFrameSx,
  resetPasswordFailedHeadingSx,
  resetPasswordFailedLogoBlockSx,
  resetPasswordFailedSubtitleContainerSx,
  resetPasswordFailedSubtitleSx,
  resetPasswordFailedTitleBlockSx,
  RESET_PASSWORD_FAILED_SUBTITLE_LINE_1,
  RESET_PW_FAILED_SUBTITLE_LINE_2,
  resetPasswordFieldErrorSx,
  resetPasswordFieldLabelSx,
  resetPasswordFormSx,
  resetPasswordHeadingFrameSx,
  resetPasswordHeadingSx,
  resetPasswordInputSx,
  resetPasswordLogoBlockSx,
  resetPasswordPasswordFieldBlockSx,
  resetPasswordPrimaryButtonSx,
  resetPasswordSubtitleSx,
  resetPasswordTitleBlockSx,
} from '../styles/resetPasswordStyles'

function isTokenFailure(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  if (error.isNotFound || error.status === 410) return true
  if (error.fieldError('token')) return true
  return (
    error.code === 'bad_request' &&
    !!error.details?.some((d) => d.field === 'token')
  )
}

function getResetServerError(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'An unexpected error occurred. Please try again.'
  }
  if (error.status === 409 || error.code === 'conflict') {
    return 'New password must be different from your current password'
  }
  const confirmIssue = error.fieldError('confirm_password')
  if (confirmIssue) return confirmIssue
  const passwordIssue = error.fieldError('new_password')
  if (passwordIssue) return passwordIssue
  return error.message
}

type Step = 'loading' | 'form' | 'expired'

function resetPasswordDocumentTitle(step: Step): string {
  if (step === 'form') return 'Create New Password'
  if (step === 'expired') return 'Password Reset Failed'
  return 'Loading'
}

function RequiredLabel({
  htmlFor,
  children,
}: {
  readonly htmlFor: string
  readonly children: string
}) {
  return (
    <Typography
      component="label"
      htmlFor={htmlFor}
      sx={resetPasswordFieldLabelSx}
    >
      {children}{' '}
      <Box component="span" className="reset-password-required">
        *
      </Box>
    </Typography>
  )
}

export function ResetPasswordPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const { token: pathToken } = useParams<{ token: string }>()
  const [searchParams] = useSearchParams()
  const searchToken = searchParams.get('token')

  const token = useMemo(() => {
    const fromState = readPasswordResetTokenFromState(location.state)
    if (fromState) return fromState
    if (pathToken) return pathToken
    return searchToken
  }, [location.state, pathToken, searchToken])

  const [step, setStep] = useState<Step>(() => {
    const initial =
      readPasswordResetTokenFromState(location.state) ??
      pathToken ??
      searchToken
    return initial ? 'loading' : 'expired'
  })
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)

  const tokenValidation = useValidateResetToken(token)
  const resetPassword = useResetPassword()
  const lastUrlStripKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!token) {
      setStep((s) => (s === 'expired' ? s : 'expired'))
      return
    }
    if (tokenValidation.isError) {
      setStep('expired')
      return
    }
    if (tokenValidation.isSuccess) {
      const envelope = tokenValidation.data
      if (envelope?.success === false || envelope?.data?.expired) {
        setStep('expired')
        return
      }
      setStep('form')
      const tokenInUrl = pathToken ?? searchToken
      if (!tokenInUrl) return
      const stripKey = `${tokenInUrl}:${token}`
      if (lastUrlStripKeyRef.current === stripKey) return
      lastUrlStripKeyRef.current = stripKey
      navigate('/reset-password', {
        replace: true,
        state: { [PASSWORD_RESET_TOKEN_STATE_KEY]: token },
      })
    }
  }, [
    token,
    tokenValidation.isError,
    tokenValidation.isSuccess,
    tokenValidation.data,
    pathToken,
    searchToken,
    navigate,
  ])

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const watchedPassword = watch('password')
  const confirmMismatch =
    !!errors.confirmPassword?.message &&
    errors.confirmPassword.message === 'Passwords do not match'

  useDocumentTitle(resetPasswordDocumentTitle(step))

  const onSubmit = (data: ResetPasswordFormData) => {
    if (!token) return
    setServerError(null)
    resetPassword.mutate(
      {
        token,
        new_password: data.password,
        confirm_password: data.confirmPassword,
      },
      {
        onSuccess: () => {
          const spUserId = useSessionStore.getState().user?.id
          const isPostOtpLoginContext = useSessionStore.getState().isAuthenticated
          if (isPostOtpLoginContext && spUserId) {
            captureSpPasswordResetCompleted(spUserId)
          }
          useSessionStore.getState().clearPendingInitialPasswordSetup()
          runDetached(
            (async () => {
              try {
                await queryClient.fetchQuery({
                  queryKey: queryKeys.auth.me(),
                  queryFn: fetchAndApplySession,
                })
                navigate('/', { replace: true })
              } catch {
                showToast(SP_FORGOT_PASSWORD_LOGIN_SUCCESS_MESSAGE, 'success')
                navigate('/login', { replace: true })
              }
            })(),
          )
        },
        onError: (error) => {
          if (isTokenFailure(error)) {
            setStep('expired')
          } else {
            setServerError(getResetServerError(error))
          }
        },
      }
    )
  }

  const passwordReg = register('password')
  const confirmReg = register('confirmPassword')

  if (step === 'loading') {
    return (
      <Box
        data-testid="reset-password-page"
        sx={{
          ...resetPasswordCardSx,
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
        }}
      >
        <AuthFormCardSkeleton testId="reset-password-loading-skeleton" />
      </Box>
    )
  }

  if (step === 'expired') {
    return (
      <Box
        data-testid="reset-password-page"
        data-reset-password-state="failed"
        sx={resetPasswordFailedCardSx}
      >
        <Box
          sx={resetPasswordFailedMiddleContentSx}
          data-testid="reset-password-failed-middle-content"
        >
          <Box
            sx={resetPasswordFailedLogoBlockSx}
            data-testid="reset-password-failed-logo"
          >
            <Box
              component="img"
              src={brushfireLogo}
              alt="Brushfire"
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </Box>

          <Box
            sx={resetPasswordFailedTitleBlockSx}
            data-testid="reset-password-failed-title-block"
          >
            <Box sx={resetPasswordFailedHeadingFrameSx}>
              <Typography
                component="h1"
                sx={resetPasswordFailedHeadingSx}
                data-testid="reset-password-failed-heading"
              >
                Password Reset Failed
              </Typography>
            </Box>
            <Box
              sx={resetPasswordFailedSubtitleContainerSx}
              data-testid="reset-password-failed-subtitle"
            >
              <Typography component="p" sx={resetPasswordFailedSubtitleSx}>
                {RESET_PASSWORD_FAILED_SUBTITLE_LINE_1}
                <br />
                {RESET_PW_FAILED_SUBTITLE_LINE_2}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box data-testid="reset-password-page" sx={resetPasswordCardSx}>
      <Box sx={resetPasswordLogoBlockSx}>
        <Box
          component="img"
          src={brushfireLogo}
          alt="Brushfire"
          sx={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </Box>

      <Box
        sx={resetPasswordTitleBlockSx}
        data-testid="reset-password-title-block"
      >
        <Box sx={resetPasswordHeadingFrameSx}>
          <Typography component="h1" sx={resetPasswordHeadingSx}>
            Create New Password
          </Typography>
        </Box>
        <Typography component="p" sx={resetPasswordSubtitleSx}>
          Enter and confirm your new password below.
        </Typography>
      </Box>

      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        sx={resetPasswordFormSx}
      >
        <Box sx={resetPasswordPasswordFieldBlockSx}>
          <RequiredLabel htmlFor="new-password">Password</RequiredLabel>
          <OutlinedInput
            id="new-password"
            type={showNew ? 'text' : 'password'}
            placeholder="Enter password"
            fullWidth
            autoComplete="new-password"
            disabled={resetPassword.isPending}
            error={!!errors.password}
            aria-invalid={!!errors.password}
            inputRef={passwordReg.ref}
            name={passwordReg.name}
            onChange={passwordReg.onChange}
            onBlur={passwordReg.onBlur}
            sx={{
              ...resetPasswordInputSx,
              ...(errors.password ? resetPasswordErrorInputSx : {}),
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label={showNew ? 'Hide password' : 'Show password'}
                  onClick={() => setShowNew((v) => !v)}
                  edge="end"
                >
                  <PasswordVisibilityIcon visible={showNew} size={24} />
                </IconButton>
              </InputAdornment>
            }
          />
        </Box>

        <Box
          sx={
            confirmMismatch
              ? resetPasswordConfirmWithErrorBlockSx
              : resetPasswordConfirmFieldBlockSx
          }
        >
          <RequiredLabel htmlFor="confirm-password">
            Confirm Password
          </RequiredLabel>
          <OutlinedInput
            id="confirm-password"
            type={showConfirm ? 'text' : 'password'}
            placeholder="Re-enter password"
            fullWidth
            autoComplete="new-password"
            disabled={resetPassword.isPending}
            error={confirmMismatch || !!errors.confirmPassword}
            aria-invalid={confirmMismatch || !!errors.confirmPassword}
            inputRef={confirmReg.ref}
            name={confirmReg.name}
            onChange={confirmReg.onChange}
            onBlur={confirmReg.onBlur}
            sx={{
              ...resetPasswordInputSx,
              ...(confirmMismatch || errors.confirmPassword
                ? resetPasswordErrorInputSx
                : {}),
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label={
                    showConfirm
                      ? 'Hide confirm password'
                      : 'Show confirm password'
                  }
                  onClick={() => setShowConfirm((v) => !v)}
                  edge="end"
                >
                  <PasswordVisibilityIcon visible={showConfirm} size={24} />
                </IconButton>
              </InputAdornment>
            }
          />
          {confirmMismatch && (
            <Typography
              role="alert"
              data-testid="reset-password-mismatch-error"
              sx={resetPasswordFieldErrorSx}
            >
              {RESET_PASSWORD_MISMATCH_MSG}
            </Typography>
          )}
        </Box>

        <PasswordChecklist password={watchedPassword} />

        {serverError && (
          <Typography
            role="alert"
            data-testid="reset-password-server-error"
            sx={{ ...resetPasswordFieldErrorSx, position: 'static' }}
          >
            {serverError}
          </Typography>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disableElevation
          disabled={resetPassword.isPending}
          data-testid="reset-password-submit"
          sx={resetPasswordPrimaryButtonSx}
        >
          {resetPassword.isPending ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Save & Sign In'
          )}
        </Button>
      </Box>
    </Box>
  )
}
